import { get, put, post, callDelete } from "@/services/api.services";
import { FolderOption, SavedObject } from "../commonModels";

export enum ProductionType {
    CHEMICAL = "Chemical Peptide Synthesis",
    MICROBIAL = "Microbial Peptide Production",
}

export enum TargetPurityMicrobial {
    CRUDE = "Crude",
    PURIFIED = "Purified",
}

export enum TargetPurityChemical {
    FIFTY_PERCENT = "50%",
    SEVENTY_PERCENT = "70%",
    NINETY_PERCENT = "90%",
}

export enum OrderStatus {
    DRAFT = "Draft",
    SUBMITTED = "Submitted",
    IN_PROGRESS = "In Progress",
    COMPLETED = "Completed",
    CANCELED = "Canceled",
}

export type Order = {
    displayName: string;
    status: OrderStatus;
    productionType: ProductionType;
    benchlingFolder: string;
    requiredByDate: Date | null;
    quantityNeeded: number | null;
    quantityNeededUnits: string | null;
    quantityNetGross: "net" | "gross" | null;
    targetPurity: TargetPurityChemical | TargetPurityMicrobial | null;
    submittedBy: string;
};

export type NewOrder = Order & { peptides: string[] };

export type SavedOrder = Order &
    SavedObject<string> & {
        orderCode: string;
        submittedAt: Date | null;
        peptideCodes: string[];
    };

export type MicrobialPeptideBatchOrderItem = {
    id: string;
    orderId: string;
    peptideCode: string;
    productionResult: "Pass" | "Fail" | null;
    failureReason: MicrobialFailureReason | null;
};

export enum MicrobialFailureReason {
    DNA_SYNTHESIS_FAILURE = "DNA Synthesis Failure",
    CLONING_FAILURE = "Cloning Failure",
}

export type OrderedPeptideValidationResult = {
    valid: boolean;
    invalidPeptideCodes: string[];
};

export type OrderResult = {
    order: Order;
};

export type UpdatePeptideOrderResult = OrderResult & {
    removedPeptideCodes: string[];
    keptPeptideCodes: string[];
    addedPeptideCodes: string[];
};

export type UploadOrderDetailsResult = {
    updatedRows: number;
    skippedRowMessages: string[];
};

export const ordersAPI = {
    fetchAllOrders: async (): Promise<SavedOrder[]> => {
        return get("/orders");
    },
    fetchOpenOrdersByType: async (productionType: ProductionType): Promise<SavedOrder[]> => {
        return get(`/orders/productionType/${productionType}`);
    },
    fetchOrderFolders: async (): Promise<FolderOption[]> => {
        return get("/orders/folders");
    },
    fetchOrdersInStatuses: async (statuses: string[]) => {
        return get("/orders/inStatuses", {
            params: {
                statuses,
            },
        });
    },
    validatePeptideBatchCodes: async (
        peptideCodes: string[]
    ): Promise<OrderedPeptideValidationResult> => {
        return post(`/orders/peptide/validatePeptideCodes`, {
            peptideCodes,
        });
    },
    validatePeptides: async (
        peptideCodes: string[],
        orderIds: string[]
    ): Promise<OrderedPeptideValidationResult> => {
        return post(`/orders/peptide/validatePeptidesForOrders`, {
            peptideCodes,
            orderIds,
        });
    },
    createPeptideOrder: async (order: NewOrder): Promise<OrderResult> => {
        return post("/orders/peptide", order);
    },
    updatePeptideOrder: async (
        order: Partial<Order> & { id: string },
        peptides?: string[]
    ): Promise<OrderResult> => {
        return put(`/orders/peptide/${order.id}`, { order, peptides });
    },
    deletePeptideOrder: async (orderId: string) => {
        return callDelete(`/orders/peptide/${orderId}`);
    },
    downloadOrderDetails: async (orderId: string) => {
        return get(`/orders/details/${orderId}`, {
            responseType: "blob",
        });
    },
    uploadOrderDetails: async (orderId: string, file): Promise<UploadOrderDetailsResult> => {
        const formData = new FormData();
        formData.set("file", file);
        return post(`/orders/details/${orderId}`, formData);
    },
    fetchOrderItems: async (orderId: string): Promise<MicrobialPeptideBatchOrderItem[]> => {
        return get(`/orders/items/${orderId}`);
    },
    updateOrderItems: async (
        orderId: string,
        orderItems: MicrobialPeptideBatchOrderItem[]
    ): Promise<MicrobialPeptideBatchOrderItem[]> => {
        return put(`/orders/items/${orderId}`, { orderItems });
    },
};
